import { Directive, EventEmitter, HostListener, Input, Output } from '@angular/core';

/** Данная директива предназначена для горизонтального свайпа
 * @Input listening - флаг для активации отслеживания свайпа (по умолчанию true)
 * @Input threshold - порог в пикселях для срабатывания свайпа (по умолчанию 50)
 * @Input maxWidth - максимальная ширина свайпа в пикселях (по умолчанию 100)
 * @Input direction - направление свайпа: 'left' (только влево), 'right' (только вправо), 'both' (оба направления, по умолчанию)
 * @Input smoothing - коэффициент EMA-сглаживания движения [0..1]: 1 = без сглаживания, 0 = максимальное
 * @Output swipeMove - эмитит текущее смещение в пикселях во время свайпа
 * @Output swipeEnd - эмитит когда свайп завершен и прошел порог (true = прошел порог, false = не прошел)
 * @Output swipeDirection - эмитит направление свайпа: 'left' или 'right'
 */

@Directive({
  selector: '[sdSwipeX]',
  standalone: true,
})
export class SwipeXDirective {
  @Input() listening = true;
  @Input() threshold = 50;
  @Input() maxWidth = 100;
  @Input() direction: 'left' | 'right' | 'both' = 'both';
  @Input() smoothing = 0.2;

  @Output() swipeMove = new EventEmitter<number>();
  @Output() swipeEnd = new EventEmitter<boolean>();
  @Output() swipeDirection = new EventEmitter<'left' | 'right'>();

  private touchStartX = 0;
  private currentDirection: 'left' | 'right' | null = null;
  private smoothedX = 0;
  private pendingMoveX: number | null = null;
  private rafId: number | null = null;

  @HostListener('touchstart', ['$event'])
  onTouchStart(e: TouchEvent): void {
    if (!this.listening) return;

    this.touchStartX = e.changedTouches[0].clientX;
    this.currentDirection = null;
    this.smoothedX = 0;
    this.cancelRaf();
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(e: TouchEvent): void {
    if (!this.listening) return;

    e.stopPropagation();
    const currentX = e.changedTouches[0].clientX;
    const diffX = currentX - this.touchStartX;

    if (!this.currentDirection && Math.abs(diffX) > 10) {
      this.currentDirection = diffX > 0 ? 'right' : 'left';
    }

    const shouldMove =
      this.direction === 'both' ||
      (this.direction === 'left' && this.currentDirection === 'left') ||
      (this.direction === 'right' && this.currentDirection === 'right');

    if (!shouldMove) return;

    let moveX: number;

    if (this.direction === 'left' && this.currentDirection === 'left') {
      moveX = Math.max(-this.maxWidth, Math.min(0, diffX));
    } else if (this.direction === 'right' && this.currentDirection === 'right') {
      moveX = Math.min(this.maxWidth, Math.max(0, diffX));
    } else {
      moveX = Math.max(-this.maxWidth, Math.min(this.maxWidth, diffX));
    }

    this.pendingMoveX = moveX;
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => this.flushMove());
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(e: TouchEvent): void {
    if (!this.listening || !this.currentDirection) return;

    this.cancelRaf();

    const currentX = e.changedTouches[0].clientX;
    const diffX = currentX - this.touchStartX;
    const absDiffX = Math.abs(diffX);

    const passedThreshold = absDiffX >= this.threshold;
    this.swipeEnd.emit(passedThreshold);

    if (passedThreshold) {
      this.swipeDirection.emit(this.currentDirection);
    }

    this.touchStartX = 0;
    this.currentDirection = null;
    this.smoothedX = 0;
  }

  reset(): void {
    this.cancelRaf();
    this.touchStartX = 0;
    this.currentDirection = null;
    this.smoothedX = 0;
    this.swipeMove.emit(0);
  }

  private flushMove(): void {
    this.rafId = null;
    if (this.pendingMoveX === null) return;

    const alpha = Math.max(0, Math.min(1, this.smoothing));
    this.smoothedX = alpha * this.pendingMoveX + (1 - alpha) * this.smoothedX;
    this.pendingMoveX = null;

    this.swipeMove.emit(Math.round(this.smoothedX));
  }

  private cancelRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pendingMoveX = null;
  }
}
